import { useEffect, useState } from 'react';
import type { MedplumClient } from '@medplum/core';
import type { Patient } from '@medplum/fhirtypes';
import type { Contact } from '../pages/clients/data/contactTypes';
import {
  patientContactToContact,
  relatedPersonToContact,
} from '../pages/clients/utils/clientProfileFhir';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';

export interface UseChartContactsResult {
  contacts: Contact[];
  loading: boolean;
  error: string | null;
}

/** Loads emergency + related person contacts for chart/portal ROI pickers. */
export function useChartContacts(
  medplum: MedplumClient,
  patientId: string | undefined,
): UseChartContactsResult {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!patientId) {
      setContacts([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const patient = await medplum.readResource('Patient', patientId);
        const fromPatient = (patient.contact ?? []).map((pc, index) =>
          patientContactToContact(pc, index),
        );

        if (cancelled) return;

        let fromRelated: Contact[] = [];
        try {
          const relatedPersons = await medplum.searchResources('RelatedPerson', {
            patient: `Patient/${patientId}`,
            _count: '100',
          });
          fromRelated = relatedPersons
            .filter((rp) => rp.active !== false)
            .map((rp) => relatedPersonToContact(rp));
        } catch {
          // RelatedPerson search may be unavailable for some portal roles; Patient.contact still works.
        }

        if (cancelled) return;
        setContacts([...fromPatient, ...fromRelated]);
      } catch (err) {
        if (!cancelled) {
          setError(getUserFacingErrorMessage(err, 'Failed to load contacts'));
          setContacts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [medplum, patientId]);

  return { contacts, loading, error };
}

export function contactDisplayLabel(contact: Contact): string {
  const name = `${contact.legalFirstName} ${contact.legalLastName}`.trim();
  const rel = contact.relationshipType ? ` (${contact.relationshipType})` : '';
  return `${name}${rel}`;
}

export function contactPrimaryPhone(contact: Contact): string | undefined {
  return contact.phones?.[0]?.value?.trim() || undefined;
}

export async function appendPatientContactFromRoiOther(
  medplum: MedplumClient,
  patient: Patient,
  params: { name: string; phone: string; relationship: string },
): Promise<{ patient: Patient; contactIndex: number }> {
  const parts = params.name.trim().split(/\s+/);
  const legalFirstName = parts[0] ?? '';
  const legalLastName = parts.length > 1 ? parts.slice(1).join(' ') : '';

  const nextContact = {
    name: {
      family: legalLastName,
      given: legalFirstName ? [legalFirstName] : undefined,
    },
    relationship: [{ text: params.relationship.trim() || 'Other' }],
    telecom: params.phone.trim()
      ? [{ system: 'phone' as const, value: params.phone.trim(), use: 'mobile' as const }]
      : undefined,
  };

  const existing = [...(patient.contact ?? [])];
  existing.push(nextContact);
  const updated = await medplum.updateResource<Patient>({
    ...patient,
    contact: existing,
  });

  return { patient: updated, contactIndex: existing.length - 1 };
}
